import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Heart, GripVertical, X, ChevronUp, ChevronDown, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function TopFivePicker() {
  const [allFavorites, setAllFavorites] = useState([]);
  const [topFive, setTopFive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [favsRes, meRes] = await Promise.all([
        authFetch(`${API}/users/me/all-favorites`),
        authFetch(`${API}/auth/me`)
      ]);
      if (favsRes.ok) setAllFavorites(await favsRes.json());
      if (meRes.ok) {
        const user = await meRes.json();
        setTopFive(user.top_five || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const addToTop = (albumId) => {
    if (topFive.length >= 5) { toast.error("Maximum 5 albums"); return; }
    if (topFive.includes(albumId)) return;
    setTopFive([...topFive, albumId]);
    setDirty(true);
  };

  const removeFromTop = (albumId) => {
    setTopFive(topFive.filter(id => id !== albumId));
    setDirty(true);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const next = [...topFive];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setTopFive(next);
    setDirty(true);
  };

  const moveDown = (index) => {
    if (index === topFive.length - 1) return;
    const next = [...topFive];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setTopFive(next);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/users/me/top-five`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ album_ids: topFive })
      });
      if (res.ok) {
        toast.success("Top 5 saved!");
        setDirty(false);
      } else {
        let msg = "Failed to save";
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        toast.error(msg);
      }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const getAlbum = (albumId) => {
    const fav = allFavorites.find(f => f.album_id === albumId);
    return fav?.album;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
      </div>
    );
  }

  const unselected = allFavorites.filter(f => !topFive.includes(f.album_id));

  return (
    <div className="space-y-5" data-testid="top-five-picker">
      {/* Current Top 5 */}
      <div>
        <p className="text-sm font-medium text-white mb-3">
          Your Top 5 ({topFive.length}/5)
        </p>
        {topFive.length === 0 ? (
          <p className="text-sm text-white/40 py-3">
            Select albums from your favorites below to build your Top 5.
          </p>
        ) : (
          <div className="space-y-2">
            {topFive.map((albumId, index) => {
              const album = getAlbum(albumId);
              if (!album) return null;
              const cover = album.cover_medium || album.cover_url || album.cover_small || "";
              return (
                <div key={albumId} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 group"
                  data-testid={`top-five-slot-${index}`}>
                  <span className="text-lg font-bold text-purple-400 w-6 text-center">{index + 1}</span>
                  <img src={cover} alt={album.title}
                    className="w-10 h-10 rounded object-cover bg-white/5"
                    onError={(e) => { e.target.src = "https://placehold.co/40x40/1a1a2e/9333ea?text=?"; }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{album.title}</p>
                    <p className="text-xs text-white/50 truncate">{album.artist}</p>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => moveUp(index)} disabled={index === 0}
                      className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white disabled:opacity-20"
                      data-testid={`top-five-up-${index}`}>
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveDown(index)} disabled={index === topFive.length - 1}
                      className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white disabled:opacity-20"
                      data-testid={`top-five-down-${index}`}>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeFromTop(albumId)}
                      className="p-1 rounded hover:bg-red-500/20 text-white/50 hover:text-red-400"
                      data-testid={`top-five-remove-${index}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Save Button */}
      {dirty && (
        <Button onClick={handleSave} disabled={saving} className="rounded-full w-full"
          data-testid="save-top-five-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          {saving ? "Saving..." : "Save Top 5"}
        </Button>
      )}

      {/* Available favorites to add */}
      {allFavorites.length === 0 ? (
        <p className="text-sm text-white/40 py-2">
          You haven't favorited any albums yet. Use the heart button on albums to add them here.
        </p>
      ) : unselected.length > 0 && topFive.length < 5 ? (
        <div>
          <p className="text-sm font-medium text-white/60 mb-3">
            Your Favorites ({unselected.length} available)
          </p>
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
            {unselected.map((fav) => {
              const album = fav.album;
              if (!album) return null;
              const cover = album.cover_medium || album.cover_url || album.cover_small || "";
              return (
                <button key={fav.album_id} onClick={() => addToTop(fav.album_id)}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-purple-500/10 hover:ring-1 hover:ring-purple-500/30 transition-all text-left"
                  data-testid={`add-to-top-${fav.album_id}`}>
                  <img src={cover} alt={album.title}
                    className="w-8 h-8 rounded object-cover bg-white/5 flex-shrink-0"
                    onError={(e) => { e.target.src = "https://placehold.co/32x32/1a1a2e/9333ea?text=?"; }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{album.title}</p>
                    <p className="text-[10px] text-white/50 truncate">{album.artist}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : topFive.length >= 5 ? (
        <p className="text-sm text-white/40">All 5 slots filled. Remove one to swap in another album.</p>
      ) : null}
    </div>
  );
}
